import React from 'react';
import clsx from 'clsx';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { Typography } from '@/components/common';
import { CircleStatus } from './CircleStatus';
import { ChevronDown } from 'lucide-react';
import {
	StatusPhase,
	StepNameCase,
	StepStatusCase,
	StepStatusCaseType,
} from '@/interfaces';
import { useStepStatus } from '@/hooks';
import { format, parseISO } from 'date-fns';

interface Props {
	id?: string;
	title?: string;
	label: StepNameCase;
	phase: StatusPhase | undefined;
	step?: string;
	stepCase?: StepStatusCase;
	statusStep: StepStatusCaseType;
	children: React.ReactElement;
	isErrorMessage?: boolean;
	setIsErrorMessage: (value: boolean) => void;
	currentOpenStep: string[] | undefined;
	setCurrentOpenStep: React.Dispatch<
		React.SetStateAction<string[] | undefined>
	>;
	accordionContentClassName?: string;
	accordionContainerClassName?: string;
}

export const AccordionElement: React.FC<Props> = ({
	id,
	label,
	step,
	phase,
	title,
	stepCase,
	statusStep,
	children,
	isErrorMessage,
	setIsErrorMessage,
	currentOpenStep,
	setCurrentOpenStep,
	accordionContentClassName,
	accordionContainerClassName,
}) => {
	const { stepStatus } = useStepStatus({
		stepName: label,
		caseSteps: phase?.steps,
	});
	const itemRef = React.useRef<HTMLDivElement | null>(null);
	const open = currentOpenStep?.includes(label) ?? false;
	const [errorMessage, setErrorMessage] = React.useState<string>('');

	React.useEffect(() => {
		if (step) {
			setCurrentOpenStep([step]);
		}
	}, [step]);

	React.useEffect(() => {
		if (isErrorMessage) {
			if (statusStep === 'incomplete' || statusStep === 'default') {
				setErrorMessage(
					'Please complete the section to advance to the next phase.'
				);
			} else if (statusStep === 'completed') {
				setErrorMessage(
					'Please approve the section to advance to the next phase.'
				);
			}
			setTimeout(() => {
				setErrorMessage(''); // Ocultar el mensaje después de 10 segundos
				setIsErrorMessage(false);
			}, 10000);
		}
	}, [isErrorMessage]);

	const completedStepDate =
		stepCase?.status === 'approved' ? stepCase.updated_at : undefined;

	return (
		<>
			<Accordion
				id={id}
				type="multiple"
				value={currentOpenStep}
				onValueChange={setCurrentOpenStep}
				className={clsx('w-full bg-white border rounded-2xl ', {
					'border-main-red border-2': !!errorMessage && !!isErrorMessage,
					'border-green-60':
						stepStatus === 'approved' && !open && !errorMessage,
				})}
			>
				<AccordionItem
					ref={itemRef}
					value={label}
					className="border-none w-full"
				>
					<AccordionTrigger
						withChevron={false}
						onClick={() => {
							setErrorMessage('');
							setIsErrorMessage(false);
						}}
						className={clsx(
							' rounded-2xl px-4 pt-4 pb-[18px] bg-white relative !no-underline group w-full focus-visible:outline-none',
							{
								'bg-[#A0B2451A]':
									stepStatus === 'approved' && !open && !errorMessage,
								// 'active:bg-green-base hover:border-2': !open && !errorMessage,
								'!border-b-0 rounded-b-none': open,
							}
						)}
					>
						<div className="flex flex-col items-start w-full">
							<div className="flex items-center justify-between w-full">
								<div className="flex items-stretch gap-x-[18px]">
									<CircleStatus
										position={id}
										status={
											label === 'Case Details' && stepStatus === 'completed'
												? 'approved'
												: stepStatus
										}
									/>
									<div>
										<Typography
											type="semibold-16"
											className={clsx('text-left')}
										>
											{title ?? label}
										</Typography>
										<Typography
											type="regular-12"
											className={clsx('text-left mt-1 text-grey-40', {
												'!text-green-60':
													!open && stepCase?.status === 'approved',
											})}
										>
											{completedStepDate
												? `Completed on ${format(
														parseISO(completedStepDate),
														'MMM dd, yyyy'
												  )}`
												: 'Pending previous step completion'}
										</Typography>
									</div>
								</div>
								<ChevronDown
									className={clsx(
										'h-6 w-6 shrink-0 transition-transform duration-200 opacity-40',
										{ 'rotate-180': open }
									)}
								/>
							</div>
						</div>
					</AccordionTrigger>
					<AccordionContent
						classNameContainer={accordionContainerClassName}
						className={clsx(
							'w-full pl-[42px] xl:pl-[66px] pr-4 pb-8 overflow-y-auto',
							accordionContentClassName
						)}
						onClick={() => {
							// e.stopPropagation();
							// e.preventDefault();
						}}
					>
						{React.cloneElement(children, { stepStatus })}
					</AccordionContent>
				</AccordionItem>
			</Accordion>
			{isErrorMessage && errorMessage && (
				<Typography
					type="regular-16"
					className={clsx(
						'text-main-red !mt-2 transition-opacity duration-500 ease-in-out transform',
						isErrorMessage
							? 'opacity-100 translate-y-0'
							: 'opacity-0 -translate-y-2'
					)}
				>
					{errorMessage}
				</Typography>
			)}
		</>
	);
};
